import { Card, CardBody, CardHeader, Chip, Spinner, Switch, Tooltip } from '@nextui-org/react'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button } from '~/components/common/Button'
import { Input } from '~/components/common/Input'
import { Modal, ModalBody, ModalFooter } from '~/components/modals/CurieModal'
import {
  AppConfigSecret,
  deleteRuntimeConfig,
  getAppConfigSecret,
  getRuntimeConfig,
  RuntimeConfigItem,
  seedRuntimeConfig,
  SeedResult,
  setRuntimeConfig
} from '~/services/superAdminService'
import { BetterToast } from '~/utils/toast'

const MASKED_VALUE = '********'

const SecretsManager: React.FC = () => {
  // SM state
  const [smConfig, setSmConfig] = useState<AppConfigSecret | null>(null)
  const [isLoadingSm, setIsLoadingSm] = useState(false)
  const [smFilter, setSmFilter] = useState('')

  // DB state
  const [dbConfigs, setDbConfigs] = useState<RuntimeConfigItem[]>([])
  const [isLoadingDb, setIsLoadingDb] = useState(false)
  const [dbFilter, setDbFilter] = useState('')

  // DB editing state
  const [editingKey, setEditingKey] = useState<string | null>(null)
  const [editValue, setEditValue] = useState('')
  const [isSaving, setIsSaving] = useState(false)

  // DB add key
  const [showAddModal, setShowAddModal] = useState(false)
  const [newKey, setNewKey] = useState('')
  const [newValue, setNewValue] = useState('')
  const [isAdding, setIsAdding] = useState(false)

  // DB delete
  const [deleteKeyTarget, setDeleteKeyTarget] = useState<string | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  // Seed
  const [isSeeding, setIsSeeding] = useState(false)
  const [seedResult, setSeedResult] = useState<SeedResult | null>(null)

  const fetchSmConfig = useCallback(async () => {
    try {
      setIsLoadingSm(true)
      const data = await getAppConfigSecret()
      setSmConfig(data)
    } catch (error) {
      BetterToast.error(`Failed to load secrets: ${error}`)
    } finally {
      setIsLoadingSm(false)
    }
  }, [])

  const fetchDbConfig = useCallback(async () => {
    try {
      setIsLoadingDb(true)
      const data = await getRuntimeConfig()
      setDbConfigs(data.configs)
    } catch (error) {
      BetterToast.error(`Failed to load runtime config: ${error}`)
    } finally {
      setIsLoadingDb(false)
    }
  }, [])

  useEffect(() => {
    fetchSmConfig()
    fetchDbConfig()
  }, [fetchSmConfig, fetchDbConfig])

  const handleRefresh = () => {
    fetchSmConfig()
    fetchDbConfig()
  }

  // --- SM helpers ---

  const smEntries =
    smConfig?.is_json && typeof smConfig.data === 'object'
      ? Object.entries(smConfig.data as Record<string, unknown>).filter(([key]) => {
          if (!smFilter.trim()) return true
          return key.toLowerCase().includes(smFilter.toLowerCase())
        })
      : []

  const renderSmValue = (value: unknown): string => {
    if (value === null || value === undefined) return 'null'
    if (typeof value === 'string') return value
    return JSON.stringify(value)
  }

  const truncate = (value: string, maxLen = 80): string => {
    if (value.length <= maxLen) return value
    return value.substring(0, maxLen) + '...'
  }

  // --- DB helpers ---

  const filteredDbConfigs = useMemo(() => {
    if (!dbFilter.trim()) return dbConfigs
    const lower = dbFilter.toLowerCase()
    return dbConfigs.filter(
      c => c.key.toLowerCase().includes(lower) || String(c.value).toLowerCase().includes(lower)
    )
  }, [dbConfigs, dbFilter])

  const handleToggleBoolean = async (key: string, currentValue: unknown) => {
    try {
      setIsSaving(true)
      await setRuntimeConfig(key, !currentValue)
      BetterToast.success(`"${key}" set to ${!currentValue}`)
      await fetchDbConfig()
    } catch (error) {
      BetterToast.error(`Failed to update: ${error}`)
    } finally {
      setIsSaving(false)
    }
  }

  const handleStartEdit = (key: string, currentValue: unknown) => {
    setEditingKey(key)
    if (currentValue === null || currentValue === undefined) {
      setEditValue('')
    } else if (typeof currentValue === 'string') {
      setEditValue(currentValue)
    } else {
      setEditValue(JSON.stringify(currentValue, null, 2))
    }
  }

  const handleSaveEdit = async () => {
    if (!editingKey) return
    try {
      setIsSaving(true)
      let parsedValue: unknown = editValue
      try {
        parsedValue = JSON.parse(editValue)
      } catch {
        // keep as string
      }
      await setRuntimeConfig(editingKey, parsedValue)
      BetterToast.success(`Updated "${editingKey}"`)
      await fetchDbConfig()
      setEditingKey(null)
      setEditValue('')
    } catch (error) {
      BetterToast.error(`Failed to save: ${error}`)
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async (key: string) => {
    try {
      setIsDeleting(true)
      await deleteRuntimeConfig(key)
      BetterToast.success(`Deleted "${key}"`)
      await fetchDbConfig()
      setDeleteKeyTarget(null)
    } catch (error) {
      BetterToast.error(`Failed to delete: ${error}`)
    } finally {
      setIsDeleting(false)
    }
  }

  const handleAddKey = async () => {
    if (!newKey.trim()) return
    try {
      setIsAdding(true)
      let parsedValue: unknown = newValue
      try {
        parsedValue = JSON.parse(newValue)
      } catch {
        // keep as string
      }
      await setRuntimeConfig(newKey.trim(), parsedValue)
      BetterToast.success(`Added "${newKey.trim()}"`)
      await fetchDbConfig()
      setShowAddModal(false)
      setNewKey('')
      setNewValue('')
    } catch (error) {
      BetterToast.error(`Failed to add: ${error}`)
    } finally {
      setIsAdding(false)
    }
  }

  const handleSeed = async () => {
    try {
      setIsSeeding(true)
      const result = await seedRuntimeConfig()
      setSeedResult(result)
      BetterToast.success(result.message)
      await fetchDbConfig()
    } catch (error) {
      BetterToast.error(`Failed to seed: ${error}`)
    } finally {
      setIsSeeding(false)
    }
  }

  const renderDbValue = (item: RuntimeConfigItem) => {
    if (item.type === 'boolean') {
      return (
        <Switch
          isSelected={!!item.value}
          onValueChange={() => handleToggleBoolean(item.key, item.value)}
          size='sm'
          isDisabled={isSaving}
        />
      )
    }

    if (item.type === 'list' && Array.isArray(item.value)) {
      const list = item.value as string[]
      return (
        <div className='flex flex-wrap gap-1'>
          {list.length === 0 ? (
            <span className='text-xs text-tokens-neutral-text-secondary italic'>empty list</span>
          ) : (
            list.slice(0, 5).map((v, i) => (
              <Chip key={i} size='sm' variant='flat'>
                {String(v)}
              </Chip>
            ))
          )}
          {list.length > 5 && (
            <Chip size='sm' variant='flat' color='default'>
              +{list.length - 5} more
            </Chip>
          )}
        </div>
      )
    }

    const display =
      item.value === null || item.value === undefined
        ? 'not set'
        : typeof item.value === 'string'
          ? item.value
          : JSON.stringify(item.value)

    return (
      <Tooltip content={display} placement='top' isDisabled={display.length < 60}>
        <span
          className={`text-xs font-mono ${
            item.value === null || item.value === undefined
              ? 'text-tokens-neutral-text-secondary italic'
              : 'text-tokens-neutral-text-primary'
          }`}
        >
          {display.length > 80 ? display.substring(0, 80) + '...' : display}
        </span>
      </Tooltip>
    )
  }

  const isLoading = isLoadingSm || isLoadingDb

  return (
    <div className='space-y-6'>
      {/* Page Header */}
      <Card>
        <CardHeader>
          <div className='flex justify-between items-center w-full'>
            <h2 className='text-xl font-bold'>Configuration Overview</h2>
            <Button variant='outlined' onClick={handleRefresh} disabled={isLoading}>
              {isLoading ? <Spinner size='sm' /> : 'Refresh'}
            </Button>
          </div>
        </CardHeader>
        <CardBody>
          <p className='text-sm text-tokens-neutral-text-secondary'>
            Manage all application configuration from one place. Secret credentials are stored in
            AWS Secrets Manager and are read-only here. Runtime settings are stored in the database
            and can be edited directly below.
          </p>
        </CardBody>
      </Card>

      {/* ================================================================ */}
      {/* Secrets Manager — read-only */}
      {/* ================================================================ */}
      <Card>
        <CardHeader>
          <div className='flex justify-between items-center w-full'>
            <div className='flex items-center gap-3'>
              <h3 className='text-lg font-semibold'>Secrets Manager</h3>
              <Chip size='sm' variant='flat' color='danger'>
                Read-only
              </Chip>
              {smConfig && (
                <Chip size='sm' variant='flat' color='default'>
                  {smEntries.length} keys
                </Chip>
              )}
            </div>
            <div className='w-64'>
              <Input
                type='text'
                placeholder='Filter keys...'
                value={smFilter}
                onChange={e => setSmFilter(e.target.value)}
              />
            </div>
          </div>
        </CardHeader>
        <CardBody>
          <p className='text-sm text-tokens-neutral-text-secondary mb-3'>
            Sensitive credentials (JWT, Stripe, OAuth, API keys) stored in AWS Secrets Manager. To
            prevent accidental disruptions, edit these through the AWS Console or CI/CD pipeline.
            Changes require a pod restart.
          </p>
          {smConfig?.name && (
            <p className='text-xs font-mono text-tokens-neutral-text-secondary mb-3'>
              Source: {smConfig.name}
            </p>
          )}

          {isLoadingSm ? (
            <div className='flex justify-center py-6'>
              <Spinner />
            </div>
          ) : smEntries.length === 0 ? (
            <p className='text-sm text-tokens-neutral-text-secondary text-center py-4'>
              {smFilter ? 'No matching keys' : 'No secret keys found'}
            </p>
          ) : (
            <div className='border rounded-lg overflow-hidden'>
              <table className='w-full text-sm'>
                <thead className='bg-tokens-neutral-bg-grey-1 sticky top-0'>
                  <tr>
                    <th className='text-left px-4 py-2 font-semibold text-tokens-neutral-text-primary w-[280px]'>
                      Key
                    </th>
                    <th className='text-left px-4 py-2 font-semibold text-tokens-neutral-text-primary'>
                      Value
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {smEntries.map(([key, value]) => (
                    <tr
                      key={key}
                      className='border-t border-tokens-neutral-border-2 hover:bg-tokens-neutral-bg-grey-1 transition-colors'
                    >
                      <td className='px-4 py-2 font-mono text-xs text-tokens-neutral-text-primary align-top'>
                        {key}
                      </td>
                      <td className='px-4 py-2 align-top'>
                        <Tooltip content={renderSmValue(value)} placement='top'>
                          <span
                            className={`text-xs font-mono break-all ${
                              value === MASKED_VALUE
                                ? 'text-tokens-neutral-text-secondary italic'
                                : 'text-tokens-neutral-text-primary'
                            }`}
                          >
                            {truncate(renderSmValue(value))}
                          </span>
                        </Tooltip>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      {/* ================================================================ */}
      {/* Database Runtime Config — editable */}
      {/* ================================================================ */}
      <Card>
        <CardHeader>
          <div className='flex justify-between items-center w-full'>
            <div className='flex items-center gap-3'>
              <h3 className='text-lg font-semibold'>Runtime Config</h3>
              <Chip size='sm' variant='flat' color='success'>
                Database
              </Chip>
              <Chip size='sm' variant='flat' color='default'>
                {dbConfigs.length} keys
              </Chip>
            </div>
            <div className='flex gap-2 items-center'>
              <Button
                variant='outlined'
                className='text-xs'
                onClick={handleSeed}
                disabled={isSeeding}
              >
                {isSeeding ? <Spinner size='sm' /> : 'Seed from Config File'}
              </Button>
              <Button variant='outlined' className='text-xs' onClick={() => setShowAddModal(true)}>
                + Add Key
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardBody>
          <p className='text-sm text-tokens-neutral-text-secondary mb-3'>
            Feature flags, user lists, and toggles stored in the database. Changes take effect
            immediately — no restart required. Use "Seed from Config File" to import initial values
            from the Secrets Manager config file.
          </p>

          {isLoadingDb ? (
            <div className='flex justify-center py-6'>
              <Spinner />
            </div>
          ) : dbConfigs.length === 0 ? (
            <div className='text-center py-12'>
              <p className='text-tokens-neutral-text-secondary mb-4'>
                No runtime config in the database yet.
              </p>
              <Button variant='primary' onClick={handleSeed} disabled={isSeeding}>
                {isSeeding ? <Spinner size='sm' /> : 'Import from Config File'}
              </Button>
              <p className='text-xs text-tokens-neutral-text-secondary mt-2'>
                Imports all non-secret values from the config file into the database.
              </p>
            </div>
          ) : (
            <>
              <div className='mb-4'>
                <Input
                  type='text'
                  placeholder='Search keys or values...'
                  value={dbFilter}
                  onChange={e => setDbFilter(e.target.value)}
                />
              </div>

              <div className='border rounded-lg overflow-hidden'>
                <table className='w-full text-sm'>
                  <thead className='bg-tokens-neutral-bg-grey-1'>
                    <tr>
                      <th className='text-left px-4 py-2 font-semibold w-[280px]'>Key</th>
                      <th className='text-left px-4 py-2 font-semibold'>Value</th>
                      <th className='text-center px-4 py-2 font-semibold w-[80px]'>Type</th>
                      <th className='text-right px-4 py-2 font-semibold w-[140px]'>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDbConfigs.map(item => (
                      <tr
                        key={item.key}
                        className='border-t border-tokens-neutral-border-2 hover:bg-tokens-neutral-bg-grey-1 transition-colors'
                      >
                        <td className='px-4 py-2 align-top'>
                          <span className='font-mono text-xs text-tokens-neutral-text-primary'>
                            {item.key}
                          </span>
                        </td>
                        <td className='px-4 py-2 align-top'>
                          {editingKey === item.key ? (
                            <div className='flex flex-col gap-2'>
                              <textarea
                                className='w-full border rounded-md p-2 text-xs font-mono min-h-[60px] resize-y'
                                value={editValue}
                                onChange={e => setEditValue(e.target.value)}
                                autoFocus
                              />
                              <div className='flex gap-2'>
                                <Button
                                  variant='primary'
                                  className='text-xs px-3 py-1'
                                  onClick={handleSaveEdit}
                                  disabled={isSaving}
                                >
                                  {isSaving ? <Spinner size='sm' /> : 'Save'}
                                </Button>
                                <Button
                                  variant='outlined'
                                  className='text-xs px-3 py-1'
                                  onClick={() => {
                                    setEditingKey(null)
                                    setEditValue('')
                                  }}
                                >
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          ) : (
                            renderDbValue(item)
                          )}
                        </td>
                        <td className='px-4 py-2 text-center align-top'>
                          <Chip size='sm' variant='flat' color='default'>
                            {item.type}
                          </Chip>
                        </td>
                        <td className='px-4 py-2 text-right align-top'>
                          {editingKey !== item.key && (
                            <div className='flex gap-1 justify-end'>
                              {item.type !== 'boolean' && (
                                <Button
                                  variant='outlined'
                                  className='text-xs px-3 py-1'
                                  onClick={() => handleStartEdit(item.key, item.value)}
                                >
                                  Edit
                                </Button>
                              )}
                              <Button
                                variant='outlined'
                                className='text-xs px-3 py-1 text-danger border-danger hover:bg-danger-50'
                                onClick={() => setDeleteKeyTarget(item.key)}
                              >
                                Delete
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                    {filteredDbConfigs.length === 0 && (
                      <tr>
                        <td
                          colSpan={4}
                          className='text-center py-8 text-tokens-neutral-text-secondary'
                        >
                          No matching configs.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className='mt-3 text-xs text-tokens-neutral-text-secondary'>
                {dbConfigs.length} key(s) in database
              </div>
            </>
          )}
        </CardBody>
      </Card>

      {/* Add Key Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false)
          setNewKey('')
          setNewValue('')
        }}
        hideCloseButton={false}
      >
        <ModalBody>
          <h3 className='text-lg font-semibold mb-4'>Add Config Key</h3>
          <div className='space-y-4'>
            <div>
              <label className='text-sm font-medium block mb-1'>Key</label>
              <Input
                type='text'
                placeholder='MY_CONFIG_KEY'
                value={newKey}
                onChange={e => setNewKey(e.target.value)}
                autoFocus
              />
            </div>
            <div>
              <label className='text-sm font-medium block mb-1'>Value</label>
              <textarea
                className='w-full border rounded-md p-2 text-sm font-mono min-h-[80px] resize-y'
                placeholder='Value (text, JSON array, JSON object, true/false)'
                value={newValue}
                onChange={e => setNewValue(e.target.value)}
              />
              <p className='text-xs text-tokens-neutral-text-secondary mt-1'>
                JSON values are parsed automatically. For lists: ["a@x.com", "b@x.com"]
              </p>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            variant='outlined'
            onClick={() => {
              setShowAddModal(false)
              setNewKey('')
              setNewValue('')
            }}
          >
            Cancel
          </Button>
          <Button variant='primary' onClick={handleAddKey} disabled={isAdding || !newKey.trim()}>
            {isAdding ? <Spinner size='sm' /> : 'Add'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Confirmation */}
      <Modal
        isOpen={deleteKeyTarget !== null}
        onClose={() => setDeleteKeyTarget(null)}
        hideCloseButton={false}
      >
        <ModalBody>
          <h3 className='text-lg font-semibold mb-2'>Delete Config Key</h3>
          <p className='text-sm text-tokens-neutral-text-secondary'>
            Delete{' '}
            <code className='bg-tokens-neutral-bg-grey-1 px-1 py-0.5 rounded text-xs'>
              {deleteKeyTarget}
            </code>{' '}
            from the database? This cannot be undone.
          </p>
        </ModalBody>
        <ModalFooter>
          <Button variant='outlined' onClick={() => setDeleteKeyTarget(null)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            variant='primary'
            className='bg-danger text-white'
            onClick={() => deleteKeyTarget && handleDelete(deleteKeyTarget)}
            disabled={isDeleting}
          >
            {isDeleting ? <Spinner size='sm' /> : 'Delete'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Seed Result Modal */}
      <Modal
        isOpen={seedResult !== null}
        onClose={() => setSeedResult(null)}
        hideCloseButton={false}
        className='w-[600px]'
      >
        <ModalBody>
          <h3 className='text-lg font-semibold mb-4'>Seed Results</h3>
          {seedResult && (
            <div className='space-y-4'>
              <p className='text-sm'>{seedResult.message}</p>

              {seedResult.seeded.length > 0 && (
                <div>
                  <h4 className='text-sm font-semibold text-success mb-1'>
                    Imported ({seedResult.seeded.length})
                  </h4>
                  <div className='flex flex-wrap gap-1'>
                    {seedResult.seeded.map(k => (
                      <Chip key={k} size='sm' variant='flat' color='success'>
                        {k}
                      </Chip>
                    ))}
                  </div>
                </div>
              )}

              {seedResult.skipped_secret.length > 0 && (
                <div>
                  <h4 className='text-sm font-semibold text-warning mb-1'>
                    Skipped - Secrets ({seedResult.skipped_secret.length})
                  </h4>
                  <div className='flex flex-wrap gap-1'>
                    {seedResult.skipped_secret.map(k => (
                      <Chip key={k} size='sm' variant='flat' color='warning'>
                        {k}
                      </Chip>
                    ))}
                  </div>
                </div>
              )}

              {seedResult.skipped_existing.length > 0 && (
                <div>
                  <h4 className='text-sm font-semibold text-tokens-neutral-text-secondary mb-1'>
                    Skipped - Already Exists ({seedResult.skipped_existing.length})
                  </h4>
                  <div className='flex flex-wrap gap-1'>
                    {seedResult.skipped_existing.map(k => (
                      <Chip key={k} size='sm' variant='flat'>
                        {k}
                      </Chip>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant='outlined' onClick={() => setSeedResult(null)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}

export default SecretsManager
